import React from "react";
import { Link } from "react-router-dom";
import {
  Users, FileText, Wallet, TrendingUp, Smartphone, ArrowRight,
  Car, LayoutDashboard, Activity, Sparkles, Shield, CheckCircle2
} from "lucide-react";
import PhoneMockup from "../components/site/PhoneMockup";
import { ScreenDashboard, ScreenInvoices, ScreenClients } from "../components/site/PhoneScreens";
import Screenshots from "../components/site/Screenshots";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_boostpro-demo/artifacts/nqty693k_boostpro-logo-transparent.svg";

const pillars = [
  { icon: Users, title: "Gestion des clients", desc: "Centralisez toutes les informations de vos clients en un seul endroit." },
  { icon: FileText, title: "Facturation", desc: "Créez et suivez vos factures simplement, depuis votre téléphone." },
  { icon: Wallet, title: "Dépenses professionnelles", desc: "Gardez le contrôle de vos achats et frais professionnels." },
  { icon: TrendingUp, title: "Rentabilité", desc: "Visualisez votre activité grâce à des statistiques claires." },
  { icon: Activity, title: "Suivi d'activité", desc: "Mesurez vos performances et anticipez vos prochaines décisions." },
];

const features = [
  { icon: Users, title: "Gestion clients" },
  { icon: FileText, title: "Factures" },
  { icon: Wallet, title: "Dépenses" },
  { icon: Car, title: "Véhicule" },
  { icon: Activity, title: "Statistiques" },
  { icon: LayoutDashboard, title: "Tableau de bord" },
  { icon: TrendingUp, title: "Suivi d'activité" },
];

export default function HomePage() {
  return (
    <main data-testid="page-home" className="relative">
      {/* === HERO === */}
      <section className="relative pt-32 md:pt-40 pb-24 overflow-hidden">
        {/* Background glows */}
        <div className="absolute inset-0 bg-grid opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
        <div className="absolute -top-20 -left-20 h-[480px] w-[480px] rounded-full bg-blue-600/20 blur-[120px]" />
        <div className="absolute top-10 right-0 h-[420px] w-[420px] rounded-full bg-emerald-500/15 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-7 stagger">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-400/30 bg-blue-500/10 text-blue-300 text-xs font-medium">
                <Sparkles size={12} />
                <span>Nouvelle génération de gestion mobile</span>
              </div>

              <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-white">
                <span className="text-gradient-blue">BoostPro</span>
                <span className="block mt-2 text-white">Pilotez votre activité <span className="italic font-light text-slate-300">simplement.</span></span>
              </h1>

              <p className="mt-6 text-lg md:text-xl text-slate-400 max-w-2xl leading-relaxed">
                Clients, factures, dépenses, rentabilité et suivi d'activité réunis dans une seule application
                pensée pour les <span className="text-slate-200">indépendants, artisans et petites entreprises</span>.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link
                  data-testid="hero-cta-discover"
                  to="/fonctionnalites"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-medium px-7 py-3.5 transition-all glow-blue"
                >
                  Découvrir l'application
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <span
                  data-testid="hero-cta-play"
                  className="inline-flex items-center justify-center gap-3 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-slate-200 font-medium px-7 py-3.5 transition-all"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 2.5v19l9-9.5-9-9.5z" fill="url(#pg)"/>
                    <defs>
                      <linearGradient id="pg" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#3B82F6"/>
                        <stop offset="100%" stopColor="#10B981"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Bientôt sur Google Play
                </span>
              </div>

              <div className="mt-10 flex items-center gap-6 text-xs text-slate-500">
                <div className="flex items-center gap-2"><Shield size={14} className="text-emerald-400" /> Données locales</div>
                <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400" /> Sans abonnement caché</div>
                <div className="flex items-center gap-2"><Smartphone size={14} className="text-blue-400" /> 100% mobile</div>
              </div>
            </div>

            {/* Phone visual */}
            <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
              <div className="absolute -inset-10 bg-gradient-to-tr from-blue-600/20 via-transparent to-emerald-500/20 blur-3xl rounded-full" />
              <div className="relative">
                <PhoneMockup floating testid="hero-phone-mockup">
                  <ScreenDashboard />
                </PhoneMockup>
                {/* Floating badges */}
                <div className="hidden md:flex absolute -left-14 top-20 items-center gap-2 px-3 py-2 rounded-xl glass-strong animate-float" style={{ animationDelay: "0.5s" }}>
                  <div className="h-7 w-7 rounded-lg bg-emerald-500/20 flex items-center justify-center"><TrendingUp size={14} className="text-emerald-400" /></div>
                  <div>
                    <div className="text-[10px] text-slate-400">CA en hausse</div>
                    <div className="text-xs font-semibold text-white">+18% ce mois</div>
                  </div>
                </div>
                <div className="hidden md:flex absolute -right-10 bottom-32 items-center gap-2 px-3 py-2 rounded-xl glass-strong animate-float" style={{ animationDelay: "1.1s" }}>
                  <div className="h-7 w-7 rounded-lg bg-blue-500/20 flex items-center justify-center"><FileText size={14} className="text-blue-400" /></div>
                  <div>
                    <div className="text-[10px] text-slate-400">Facture</div>
                    <div className="text-xs font-semibold text-white">Payée — 580 €</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === PILLARS / SECTIONS === */}
      <section className="relative py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-blue-400">Fonctionnalités</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold tracking-tight text-white">
              Tout ce dont vous avez besoin, <span className="text-gradient-blue">rien de plus.</span>
            </h2>
            <p className="mt-4 text-slate-400">
              BoostPro réunit les outils essentiels à votre activité dans une expérience claire et rapide.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`pillar-card-${i}`}
                  className={`group relative rounded-2xl p-7 border border-white/5 bg-gradient-to-br from-slate-900/80 to-slate-900/40 hover:border-blue-400/30 transition-all duration-500 overflow-hidden ${i === 0 ? "lg:col-span-2" : ""}`}
                >
                  <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-blue-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 ring-1 ring-white/10">
                      <Icon size={22} className="text-blue-300" />
                    </div>
                    <h3 className="mt-5 font-display text-xl font-semibold text-white">{p.title}</h3>
                    <p className="mt-2 text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* === DUAL-SCREEN SHOWCASE === */}
      <section className="relative py-24 md:py-28 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px divider-line" />
        <div className="absolute top-1/2 -left-32 h-[400px] w-[400px] rounded-full bg-blue-600/10 blur-[120px]" />

        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 flex justify-center gap-6">
            <PhoneMockup className="-rotate-3 mt-8" testid="showcase-phone-invoices">
              <ScreenInvoices />
            </PhoneMockup>
            <PhoneMockup className="rotate-3 -mt-8 hidden md:block" testid="showcase-phone-clients">
              <ScreenClients />
            </PhoneMockup>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-400">Pensé pour le terrain</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white tracking-tight">
              Votre activité, <br className="hidden md:block" />
              <span className="text-gradient-blue">dans votre poche.</span>
            </h2>
            <p className="mt-5 text-slate-400 text-lg leading-relaxed">
              Suivez vos clients, créez une facture entre deux rendez-vous, enregistrez vos frais
              en quelques secondes — sans ordinateur, sans frictions.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Création de facture en moins de 60 secondes",
                "Suivi en temps réel des paiements et impayés",
                "Catégorisation automatique des dépenses",
                "Statistiques claires pour décider sereinement",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-300">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link
                data-testid="showcase-cta-features"
                to="/fonctionnalites"
                className="inline-flex items-center gap-2 text-blue-300 hover:text-blue-200 font-medium"
              >
                Voir toutes les fonctionnalités <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* === FEATURES PREVIEW (toolkit) === */}
      <section className="relative py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-xl">
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-blue-400">Boîte à outils</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white tracking-tight">
                Tous vos outils, en un seul endroit.
              </h2>
            </div>
            <Link
              to="/fonctionnalites"
              data-testid="features-preview-cta"
              className="inline-flex items-center gap-2 text-slate-300 hover:text-white"
            >
              Tout explorer <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="group rounded-xl border border-white/5 bg-slate-900/40 hover:bg-slate-900/80 hover:border-blue-400/30 px-4 py-5 transition-all duration-300 text-center"
                >
                  <Icon size={22} className="mx-auto text-blue-300 group-hover:text-emerald-300 transition-colors" />
                  <div className="mt-3 text-[13px] font-medium text-slate-200">{f.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* === APP SCREENSHOTS PLACEHOLDERS === */}
      <Screenshots />

      {/* === CTA === */}
      <section className="relative py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-[#0b1b34] to-slate-900 p-10 md:p-16 text-center">
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 h-40 w-40 rounded-full bg-blue-500/40 blur-3xl" />
            <div className="absolute -bottom-10 left-10 h-40 w-40 rounded-full bg-emerald-500/30 blur-3xl" />
            <img src={LOGO_URL} alt="BoostPro" className="mx-auto h-16 w-16 rounded-2xl relative" />
            <h2 className="relative mt-6 font-display text-3xl md:text-5xl font-semibold text-white tracking-tight">
              Prêt à booster votre activité ?
            </h2>
            <p className="relative mt-4 text-slate-400 max-w-xl mx-auto">
              Rejoignez les indépendants qui pilotent leur entreprise simplement, depuis leur poche.
            </p>
            <div className="relative mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                data-testid="footer-cta-contact"
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-medium px-7 py-3.5 glow-blue transition-all"
              >
                Nous contacter <ArrowRight size={18} />
              </Link>
              <span className="inline-flex items-center gap-2 text-emerald-300 text-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Bientôt sur Google Play
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
